import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MapBoxService } from '../mapbox/mapbox.service';
import {
  GeocoderSearchHistory,
  SearchGeocoderService,
} from '../../shared/services/search-geocoder.service';
import { environment } from '../../../environments/environment';
import { BreakpointObserverService } from '../../shared/services/breakpoint-observer.service';
import { NgForOf, NgIf } from '@angular/common';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { IconButtonDirective } from '../../shared/directives/icon-button.directive';
import { PopupService } from '../mapbox/services/popup.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MapStyleServiceService } from '../map-style-selector/map-style-service.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap } from 'rxjs/operators';
import { take, timer } from 'rxjs';

declare const MapboxGeocoder: typeof import('@mapbox/mapbox-gl-geocoder');
declare const mapboxgl: typeof import('mapbox-gl');

@Component({
  selector: 'app-map-search',
  templateUrl: './map-search.component.html',
  styleUrls: ['./map-search.component.scss'],
  imports: [NgIf, IconComponent, NgForOf, IconButtonDirective, MatTooltip],
})
export class MapSearchComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  @Output() searchClose = new EventEmitter<void>();
  @Output() searchOpen = new EventEmitter<void>();

  @Input() isSearchInputActive: boolean = false;


  private readonly searchGeocoder: MapboxGeocoder = new MapboxGeocoder({
    accessToken: environment.mapbox.accessToken,
    mapboxgl: mapboxgl,
    placeholder: 'Map search',
    countries: 'us',
    language: 'en',
    reverseGeocode: true,
    clearAndBlurOnEsc: true,
  });

  constructor(
    public geocoderService: SearchGeocoderService,
    private mapboxService: MapBoxService,
    private popupService: PopupService,
    private mapStyleService: MapStyleServiceService
  ) {}

  ngOnInit(): void {
    // On map style change close search input
    this.mapStyleService.$mapStyle.subscribe(() => {
      this.searchClose.emit();
    });

    this.mapboxService.mapReady$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // 200ms delay polling if map is loaded and open search if it is
        switchMap(() =>
          timer(0, 200).pipe(
            filter(() => {
              const map = this.mapboxService.map;
              return !!map && map.loaded();
            }),
            take(1)
          )
        )
      )
      .subscribe(() => {
        this.searchOpen.emit();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isSearchInputActive']) {
      if (this.mapboxService.map && this.mapboxService.map.loaded()) {
        changes['isSearchInputActive'].currentValue
          ? this.geocoderService.addGeocoderSearch(
              this.mapboxService.map,
              this.searchGeocoder,
              this.searchClose,
              'default',
              this.popupService.popup // Pass single popup instance to close popup on input click
            )
          : this.geocoderService.removeGeocoderSearch(
              this.mapboxService.map,
              this.searchGeocoder
            );
      }
    }
  }

  public handleJumpToAddress(item: GeocoderSearchHistory): void {
    this.geocoderService.flyToAddress(
      [item.lng, item.lat],
      this.mapboxService.map
    );
  }

  public handleRemove(e: MouseEvent, id: string): void {
    e.stopPropagation();
    this.geocoderService.removeSearchItemFromHistory(id, this.searchGeocoder);
  }

  public handleSearchOpenButton(): void {
    this.searchOpen.emit();
  }
}
